/**
 * Changing the document on a policy after it exists.
 *
 * Two jobs, both for authors: replace a draft's file (the edited Word
 * document coming back as the next version, or a corrected PDF), and turn a
 * published policy back into a draft so that can happen. The upload half is
 * the same pipeline the upload page uses, minus creating the policy.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';
import { ACCEPTED_TYPE, DOCX_TYPE, documentKind, probeFile, safeFileName } from './useUpload';
import type { Attachment } from './usePolicy';

export type ReplaceStage = 'idle' | 'checking' | 'uploading' | 'attaching' | 'converting' | 'clearing';

export interface ReplaceResult {
  /** Set when the file is a Word document and its conversion failed; the
   *  draft has the Word file and no PDF. */
  conversionFailed?: string;
}

/**
 * Put a new file on a draft, converting a Word document, and remove the
 * files that were there. The old ones go last, so a failure part-way leaves
 * the draft with a document rather than none.
 */
export async function replaceDraftDocument(
  orgId: string,
  policyId: string,
  file: File,
  previous: readonly Attachment[],
  onStage: (s: ReplaceStage) => void,
): Promise<ReplaceResult> {
  onStage('checking');
  const probe = await probeFile(file);
  if (!probe.ok) throw new Error(probe.reason);
  const kind = probe.kind ?? documentKind(file) ?? 'pdf';

  onStage('uploading');
  const path = `orgs/${orgId}/policies/${policyId}/drafts/current/attachments/${Date.now()}-${safeFileName(file.name)}`;
  const target = ref(getStorage(app), path);
  await uploadBytes(target, probe.bytes, { contentType: kind === 'docx' ? DOCX_TYPE : ACCEPTED_TYPE });

  /**
   * THE SWITCH IS ONE CALL, AND IT IS THE SWITCH (blocker B6).
   *
   * This used to add the new file and then delete the old ones one call at a
   * time, with which file counted as the document decided afterwards by array
   * order. A failure between the two — a dropped connection, a closed tab —
   * left the OLD file first and therefore the one that would be published,
   * while the page said the document had been replaced.
   *
   * `setDraftDocument` appends the new file and names it the document in a
   * single write. From that moment the draft is of the new file whatever
   * happens next. The removals below are tidying: they free storage, and
   * failing they leave a superseded file in the list that is not the document,
   * is not published, and changes nothing.
   */
  onStage('attaching');
  const attached = await call<unknown, { attachmentId: string }>('setDraftDocument', {
    policyId,
    attachment: { name: file.name, contentType: kind === 'docx' ? DOCX_TYPE : ACCEPTED_TYPE, size: file.size, storagePath: path },
  });

  const result: ReplaceResult = {};
  if (kind === 'docx') {
    onStage('converting');
    try {
      await call('convertDraftDocxToPdf', { policyId, attachmentId: attached.attachmentId });
    } catch (e) {
      result.conversionFailed = e instanceof Error ? e.message : (e as { message?: string })?.message ?? 'Conversion failed.';
    }
  }

  // Tidying, after the fact and best effort. Whatever happens here, the draft
  // is already of the new file.
  onStage('clearing');
  for (const old of previous) {
    try {
      await call('removeDraftAttachment', { policyId, attachmentId: old.id });
    } catch {
      /* A file left behind is a superseded file, not the document. */
    }
  }
  return result;
}

function useRefresh(orgId: string | null, policyId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['policy', orgId, policyId] });
    // The next version's files are their own document now, so their own query.
    void qc.invalidateQueries({ queryKey: ['policy-revision', orgId, policyId] });
    void qc.invalidateQueries({ queryKey: ['policies', orgId] });
  };
}

export function useReplaceDocument(orgId: string | null, policyId: string) {
  const refresh = useRefresh(orgId, policyId);
  return useMutation({
    mutationFn: (input: { file: File; previous: readonly Attachment[]; onStage: (s: ReplaceStage) => void }) =>
      replaceDraftDocument(orgId!, policyId, input.file, input.previous, input.onStage),
    onSuccess: refresh,
    onError: refresh,
  });
}

export function useConvertAttachment(orgId: string | null, policyId: string) {
  const refresh = useRefresh(orgId, policyId);
  return useMutation({
    mutationFn: (attachmentId: string) => call('convertDraftDocxToPdf', { policyId, attachmentId }),
    onSuccess: refresh,
  });
}

export function useStartRevision(orgId: string | null, policyId: string) {
  const refresh = useRefresh(orgId, policyId);
  return useMutation({
    mutationFn: () => call<unknown, { nextVersionNumber: number }>('startPolicyRevision', { policyId }),
    onSuccess: refresh,
  });
}

export function useSubmitDraft(orgId: string | null, policyId: string) {
  const refresh = useRefresh(orgId, policyId);
  return useMutation({
    mutationFn: (input: string | { changeSummary: string; workflowId?: string; workflowReason?: string }) => {
      const s = typeof input === 'string' ? { changeSummary: input } : input;
      return call('submitPolicyForReview', {
        policyId, changeSummary: s.changeSummary.trim(),
        // Only when the author chose a workflow other than their document's default.
        ...(s.workflowId ? { workflowId: s.workflowId, workflowReason: (s.workflowReason ?? '').trim() } : {}),
      });
    },
    onSuccess: refresh,
  });
}

/** Abandon a new version that has not been submitted; the published one stays. */
export function useDiscardRevision(orgId: string | null, policyId: string) {
  const refresh = useRefresh(orgId, policyId);
  return useMutation({
    mutationFn: () => call('discardPolicyRevision', { policyId }),
    onSuccess: refresh,
  });
}
